import { router, usePage } from "@inertiajs/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedCallback } from "./useDebouncedCallback";

type NavigateOptions = {
  replace?: boolean;
  only?: string[];
};

function parseSearchParams(search: string): Record<string, string> {
  const normalized = search.startsWith("?") ? search.slice(1) : search;
  return Object.fromEntries(new URLSearchParams(normalized));
}

function buildUrl(basePath: string, params: Record<string, string>): string {
  const search = new URLSearchParams(params).toString();
  return search ? `${basePath}?${search}` : basePath;
}

function mergeParams(
  current: Record<string, string>,
  patch: Record<string, string | null>,
): Record<string, string> {
  const merged = { ...current };
  for (const [key, value] of Object.entries(patch)) {
    if (value === null || value === "") {
      delete merged[key];
    } else {
      merged[key] = value;
    }
  }
  return merged;
}

function hasSearchParams(params: Record<string, string>): boolean {
  return Object.keys(params).some(
    (key) =>
      key.startsWith("filter_") ||
      key.startsWith("sort_") ||
      key === "offset" ||
      key === "page_size",
  );
}

export function useIndexNavigation(basePath: string, options: NavigateOptions = {}) {
  const { url: inertiaUrl } = usePage();
  const [search, setSearch] = useState(() => window.location.search);
  const initialFetchDone = useRef(false);
  const paramsRef = useRef<Record<string, string>>({});
  const pendingPatchRef = useRef<Record<string, string | null>>({});

  useEffect(() => {
    setSearch(window.location.search);
  }, [inertiaUrl]);

  useEffect(() => {
    function onPopState() {
      setSearch(window.location.search);
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const params = useMemo(() => parseSearchParams(search), [search]);
  paramsRef.current = params;

  function syncUrl(patch: Record<string, string | null>) {
    const merged = mergeParams(paramsRef.current, patch);
    const nextUrl = buildUrl(basePath, merged);
    window.history.replaceState(window.history.state, "", nextUrl);
    setSearch(window.location.search);
    return merged;
  }

  function fetchWithParams(
    merged: Record<string, string>,
    fetchOptions: NavigateOptions = {},
  ) {
    router.get(basePath, merged, {
      preserveState: true,
      preserveScroll: true,
      replace: fetchOptions.replace ?? options.replace ?? false,
      only: fetchOptions.only ?? options.only,
    });
  }

  function navigate(patch: Record<string, string | null>, fetchOptions?: NavigateOptions) {
    pendingPatchRef.current = {};
    debouncedFetch.cancel();
    const merged = syncUrl(patch);
    fetchWithParams(merged, fetchOptions);
  }

  const debouncedFetch = useDebouncedCallback(() => {
    const patch = pendingPatchRef.current;
    pendingPatchRef.current = {};
    const merged = syncUrl(patch);
    fetchWithParams(merged, { replace: true });
  }, 500);

  function debouncedNavigate(patch: Record<string, string | null>) {
    pendingPatchRef.current = { ...pendingPatchRef.current, ...patch };
    debouncedFetch();
  }

  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;

    const urlParams = parseSearchParams(window.location.search);
    if (!hasSearchParams(urlParams)) return;

    fetchWithParams(urlParams, { replace: true });
  }, [basePath]);

  function handleSort(field: string) {
    const key = `sort_${field}`;
    const current = params[key];
    let next: string | null;
    if (!current) next = "asc";
    else if (current === "asc") next = "desc";
    else next = null;
    navigate({ [key]: next });
  }

  function sortDirection(field: string): "asc" | "desc" | null {
    const dir = params[`sort_${field}`];
    if (dir === "asc") return "asc";
    if (dir === "desc") return "desc";
    return null;
  }

  function pagination(page: { size: number; number: number }, totalPages: number) {
    const currentPage = Math.floor(page.number / page.size) + 1;
    return {
      currentPage,
      canPrev: page.number > 0,
      canNext: currentPage < totalPages,
    };
  }

  const queryString = useMemo(() => {
    const search = new URLSearchParams(params).toString();
    return search ? `?${search}` : "";
  }, [params]);

  return { params, navigate, debouncedNavigate, handleSort, sortDirection, pagination, queryString };
}

export function useSyncedFilter(
  paramKey: string,
  navigation: ReturnType<typeof useIndexNavigation>,
) {
  const { params, debouncedNavigate } = navigation;
  const [value, setValue] = useState(params[paramKey] ?? "");

  useEffect(() => {
    setValue(params[paramKey] ?? "");
  }, [params[paramKey]]);

  function setFilter(next: string) {
    setValue(next);
    debouncedNavigate({ [paramKey]: next || null, offset: null });
  }

  return [value, setFilter] as const;
}
